<template>
  <div id="map" ref="mapContainer" class="map-container">
    <div ref="loadingIndicator" class="loading-indicator">Calculating optimal routes...</div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import LocationsService from '../services/LocationsService';
import BoundariesService from '../services/BoundariesService';
import PerformanceMonitor from '../services/PerformanceMonitor';

export default {
  name: 'MapComponent',
  props: {
    depotMode: {
      type: Boolean,
      default: false
    },
    locationCreationMode: {
      type: Boolean,
      default: false
    },
    showLocations: {
      type: Boolean,
      default: false
    },
    locationSource: {
      type: String,
      default: 'database'
    },
    selectedCollectionId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      markers: [],            // user-added markers
      depotMarker: null,
      routeLayers: [],
      directionsCache: {}, // Cache for storing routes to reduce API calls
      dbLocations: [],      // Locations loaded from database
      dbMarkers: [],        // Markers for database locations (fallback for small datasets)
      useClusteredRendering: false, // Flag to determine rendering mode
      locationThreshold: 50, // Threshold for switching to clustered rendering
      forceClusteringMode: null, // Manual override for clustering mode (null = auto, true = force cluster, false = force markers)
      enableViewportLoading: true, // Enable viewport-based loading for large datasets
      viewportLoadingThreshold: 500, // Threshold for enabling viewport loading
      loadedBounds: null,   // Currently loaded map bounds
      allLocationsCount: 0, // Total count of all locations
      regionCache: new Map(), // Cache for loaded regions
      cacheExpiry: 5 * 60 * 1000, // Cache expiry time (5 minutes)
      draw: null,           // Mapbox Draw instance
      selectedLocations: [], // Currently selected locations
      boundaries: [],       // District boundaries
      neighborhoods: [],    // Neighborhood boundaries
      selectedBoundaries: [], // Currently selected boundaries (districts or neighborhoods)
      currentBoundaryType: 'districts', // 'districts' or 'neighborhoods'
      zoomThreshold: 12,    // Zoom level threshold for switching boundary types
      showBoundaries: true, // Toggle for boundary visibility
      selectionMode: 'normal', // Selection mode: 'normal', 'polygon', 'boundary'
      currentPopup: null,   // Track currently open popup
      popupAutoCloseTimeout: null // Timeout for auto-closing popup
    };
  },
  mounted() {
    this.initializeMap();
    // Load database locations and display markers
    this.loadDbLocations();
    
    // Load district boundaries and neighborhoods after a short delay to ensure map is ready
    setTimeout(() => {
      this.loadBoundaries();
      this.loadNeighborhoods();
    }, 1000);
    
    // Add global navigation function for popup buttons
    window.navigateToLocation = (locationId) => {
      if (locationId) {
        this.$router.push({ 
          name: 'LocationDetail', 
          params: { id: locationId },
          query: { from: 'map' }
        });
      } else {
        console.error('No location ID provided for navigation');
      }
    };
  },
  methods: {
    initializeMap() {
      // Feel free to replace the token with your own from mapbox.com
      mapboxgl.accessToken = this.accessToken;
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [28.9784, 41.0082], // Default center (Istanbul)
        zoom: 10, // Default zoom
        attributionControl: false
      });

      // Add enhanced controls
      this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
      this.map.addControl(new mapboxgl.ScaleControl(), 'bottom-left');
      this.map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');
      this.map.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');
      
      // Initialize Mapbox Draw with custom rectangle mode
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        },
        defaultMode: 'simple_select',
        modes: {
          ...MapboxDraw.modes,
          draw_rectangle: this.createRectangleMode()
        }
      });
      this.map.addControl(this.draw, 'top-left');
      
      // Add custom rectangle button
      this.addRectangleControl();
      
      // Add clustering toggle button
      this.addClusteringToggleControl();
      
      // Add boundary selection control
      this.addBoundarySelectionControl();
      
      // Add click handler for location selection
      this.map.on('click', this.handleMapClick);
      
      // Add viewport change handlers for dynamic loading
      this.map.on('moveend', this.handleViewportChange);
      this.map.on('zoomend', this.handleViewportChange);
      
      // Add zoom handler for boundary switching
      this.map.on('zoomend', this.handleBoundaryZoomChange);
      
      // Wait for map to load before adding layers
      this.map.on('load', () => {
        // Add a source for our route lines
        this.map.addSource('routes', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });
        
        // Add clustered locations source (for demand locations only)
        this.map.addSource('locations', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          },
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50
        });
        
        // Add separate source for depot locations (always visible)
        this.map.addSource('depots', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });
        
        // Add boundaries source
        this.map.addSource('boundaries', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });

        // Add neighborhoods source
        this.map.addSource('neighborhoods', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });
        
        // Add layers for clustered rendering
        this.addClusteredLayers();
        
        // Add depot layer (always visible)
        this.addDepotLayer();
        
        // Add boundary layers
        this.addBoundaryLayers();
        
        // After map loads, render locations based on count
        this.renderLocations();
      });
      
      // Add draw event listeners
      this.map.on('draw.create', this.handleDrawCreate);
      this.map.on('draw.update', this.handleDrawUpdate);
      this.map.on('draw.delete', this.handleDrawDelete);
    },
    
    /**
     * Handle map clicks to add locations or set depot
     */
    handleMapClick(e) {
      console.log('Map click event:', e);
      console.log('Current selection mode:', this.selectionMode);
      
      const coordinates = [e.lngLat.lng, e.lngLat.lat];
      
      if (this.depotMode) {
        // Set depot location
        this.setDepot(coordinates);
      } else if (this.locationCreationMode) {
        // Add location only when location creation mode is enabled
        this.addLocation(coordinates);
      } else if (this.selectionMode === 'boundary') {
        // In boundary selection mode, check if we clicked on a boundary
        console.log('In boundary mode, checking for boundary features...');
        const layersToQuery = this.currentBoundaryType === 'neighborhoods' 
          ? ['neighborhoods-fill'] 
          : ['boundaries-fill'];
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: layersToQuery
        });
        console.log(`Found ${this.currentBoundaryType} features:`, features);
        
        if (features.length > 0) {
          // Manually trigger boundary click handler with original event for Ctrl detection
          this.handleBoundaryClick({
            features: features,
            originalEvent: e.originalEvent,
            point: e.point,
            lngLat: e.lngLat
          });
        } else {
          console.log('No boundary features found at click point');
        }
        return;
      }
    },
    
    /**
     * Set the depot location
     */
    setDepot(coordinates) {
      // Remove existing depot marker if any
      if (this.depotMarker) {
        this.depotMarker.remove();
      }
      
      // Create and add a new depot marker
      const el = document.createElement('div');
      el.className = 'depot-marker';
      // Create a custom marker with anchor point at the center
      this.depotMarker = new mapboxgl.Marker({
        element: el,
        anchor: 'center' // This is the key fix - sets the anchor point at the center of the marker
      })
        .setLngLat(coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(`Depot: ${coordinates[0].toFixed(4)}, ${coordinates[1].toFixed(4)}`))
        .addTo(this.map);
      
      // Emit event with depot coordinates
      this.$emit('depot-set', coordinates);
    },
    
    /**
     * Add a location marker
     */
    addLocation(coordinates) {
      // Create and add a new location marker
      const el = document.createElement('div');
      el.className = 'location-marker';
      // Create a custom marker with anchor point at the center
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center' // This is the key fix - sets the anchor point at the center of the marker
      })
        .setLngLat(coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(`Location: ${coordinates[0].toFixed(4)}, ${coordinates[1].toFixed(4)}`))
        .addTo(this.map);
      
      this.markers.push(marker);
      
      // Emit event with location coordinates
      this.$emit('location-added', coordinates);
    },
    
    /**
     * Clear all markers
     */
    clearMarkers() {
      // Remove all location markers
      this.markers.forEach(marker => marker.remove());
      this.markers = [];
      
      // Remove depot marker
      if (this.depotMarker) {
        this.depotMarker.remove();
        this.depotMarker = null;
      }
    },
    
    /**
     * Load all locations from backend API (database or collection)
     */
    async loadDbLocations() {
      try {
        // First, get the total count of locations
        await this.getLocationCount();
        
        // Decide loading strategy based on count
        if (this.enableViewportLoading && this.allLocationsCount > this.viewportLoadingThreshold) {
          // Load only locations in current viewport
          await this.loadViewportLocations();
        } else {
          // Load all locations for smaller datasets
          await this.loadAllLocations();
        }
        
        // Update boundaries on map after locations are loaded
        this.updateBoundariesOnMap();
      } catch (error) {
        console.error('Error loading DB locations:', error);
      }
    },
    
    /**
     * Get total count of locations without loading all data
     */
    async getLocationCount() {
      try {
        let locationPromise;
        
        if (this.locationSource === 'collections' && this.selectedCollectionId) {
          locationPromise = LocationsService.getCollectionLocations(this.selectedCollectionId);
        } else {
          locationPromise = LocationsService.getLocations();
        }
        
        const response = await locationPromise;
        this.allLocationsCount = (response.data || []).length;
      } catch (error) {
        console.error('Error getting location count:', error);
        this.allLocationsCount = 0;
      }
    },
    
    /**
     * Load all locations (for smaller datasets)
     */
    async loadAllLocations() {
      let locationPromise;
      
      if (this.locationSource === 'collections' && this.selectedCollectionId) {
        locationPromise = LocationsService.getCollectionLocations(this.selectedCollectionId);
      } else {
        locationPromise = LocationsService.getLocations();
      }
      
      const response = await locationPromise;
      this.dbLocations = response.data || [];
      
      // Only show locations if showLocations is true
      if (this.showLocations) {
        this.renderLocations();
      }
    },
    
    /**
     * Load locations within current viewport bounds
     */
    async loadViewportLocations() {
      if (!this.map) return;
      
      const endTiming = PerformanceMonitor.startTiming('viewportLoading');
      
      const bounds = this.map.getBounds();
      const boundingBox = {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      };
      
      try {
        // Check cache first
        const cacheKey = this.getBoundsCacheKey(boundingBox);
        const cachedData = this.getCachedRegion(cacheKey);
        
        if (cachedData) {
          PerformanceMonitor.recordCacheMetric(true); // Cache hit
          console.log('Using cached location data for bounds:', boundingBox);
          this.dbLocations = cachedData;
          this.loadedBounds = boundingBox;
          
          if (this.showLocations) {
            this.renderLocations();
          }
          
          endTiming({
            boundingBox,
            cacheHit: true,
            locationCount: cachedData.length
          });
          return;
        }
        
        PerformanceMonitor.recordCacheMetric(false); // Cache miss
        
        let response;
        
        if (this.locationSource === 'collections' && this.selectedCollectionId) {
          // For collections, still load all and filter client-side
          // TODO: Implement collection-specific bounded queries
          const collectionResponse = await LocationsService.getCollectionLocations(this.selectedCollectionId);
          const allLocations = collectionResponse.data || [];
          
          // Filter locations within bounds
          const filteredLocations = allLocations.filter(loc => {
            if (!loc.coordinates || !Array.isArray(loc.coordinates) || loc.coordinates.length < 2) {
              return false;
            }
            
            const lng = parseFloat(loc.coordinates[0]);
            const lat = parseFloat(loc.coordinates[1]);
            
            if (isNaN(lng) || isNaN(lat)) {
              return false;
            }
            
            return lng >= boundingBox.west && lng <= boundingBox.east &&
                   lat >= boundingBox.south && lat <= boundingBox.north;
          });
          
          this.dbLocations = filteredLocations;
        } else {
          // Use server-side spatial filtering for regular locations
          response = await LocationsService.getLocationsByBounds(boundingBox);
          this.dbLocations = response.data || [];
        }
        
        // Cache the result
        this.cacheRegion(cacheKey, this.dbLocations);
        
        this.loadedBounds = boundingBox;
        
        // Only show locations if showLocations is true
        if (this.showLocations) {
          this.renderLocations();
        }
        
        endTiming({
          boundingBox,
          cacheHit: false,
          locationCount: this.dbLocations.length,
          dataSource: this.locationSource
        });
        
      } catch (error) {
        console.error('Error loading viewport locations:', error);
        endTiming({
          boundingBox,
          error: error.message,
          locationCount: 0
        });
        // Fallback to loading all locations if bounded query fails
        await this.loadAllLocations();
      }
    },
    
    /**
     * Generate cache key for bounding box
     */
    getBoundsCacheKey(bounds) {
      // Round coordinates to reduce cache key variations
      const precision = 4;
      return `${bounds.north.toFixed(precision)},${bounds.south.toFixed(precision)},${bounds.east.toFixed(precision)},${bounds.west.toFixed(precision)}`;
    },
    
    /**
     * Cache region data with timestamp
     */
    cacheRegion(cacheKey, data) {
      this.regionCache.set(cacheKey, {
        data: data,
        timestamp: Date.now()
      });
      
      // Clean up expired cache entries
      this.cleanExpiredCache();
    },
    
    /**
     * Get cached region data if not expired
     */
    getCachedRegion(cacheKey) {
      const cachedEntry = this.regionCache.get(cacheKey);
      
      if (!cachedEntry) {
        return null;
      }
      
      // Check if cache entry is expired
      if (Date.now() - cachedEntry.timestamp > this.cacheExpiry) {
        this.regionCache.delete(cacheKey);
        return null;
      }
      
      return cachedEntry.data;
    },
    
    /**
     * Clean up expired cache entries
     */
    cleanExpiredCache() {
      const now = Date.now();
      for (const [key, entry] of this.regionCache.entries()) {
        if (now - entry.timestamp > this.cacheExpiry) {
          this.regionCache.delete(key);
        }
      }
    },
    
    /**
     * Clear region cache
     */
    clearRegionCache() {
      this.regionCache.clear();
    },
    
    /**
     * Handle viewport changes for dynamic loading
     */
    handleViewportChange() {
      if (!this.enableViewportLoading || 
          this.allLocationsCount <= this.viewportLoadingThreshold ||
          !this.showLocations) {
        return;
      }
      
      const currentBounds = this.map.getBounds();
      
      // Check if we need to load more data
      if (!this.loadedBounds || !this.boundsContainsBounds(this.loadedBounds, {
        north: currentBounds.getNorth(),
        south: currentBounds.getSouth(),
        east: currentBounds.getEast(),
        west: currentBounds.getWest()
      })) {
        // Debounce the loading to avoid too many requests
        clearTimeout(this.viewportLoadTimeout);
        this.viewportLoadTimeout = setTimeout(() => {
          this.loadViewportLocations();
        }, 300);
      }
    },

    /**
     * Handle zoom change for boundary layer switching
     */
    handleBoundaryZoomChange() {
      if (!this.map || this.selectionMode !== 'boundary') {
        return;
      }

      const currentZoom = this.map.getZoom();
      const shouldShowNeighborhoods = currentZoom >= this.zoomThreshold;
      const newBoundaryType = shouldShowNeighborhoods ? 'neighborhoods' : 'districts';

      // Only switch if boundary type has changed
      if (newBoundaryType !== this.currentBoundaryType) {
        console.log(`Switching boundary type from ${this.currentBoundaryType} to ${newBoundaryType} at zoom ${currentZoom}`);
        
        // Clear current selections when switching boundary types
        this.selectedBoundaries = [];
        this.$emit('boundary-selected', []);
        this.filterLocationsByBoundary();

        // Update boundary type
        this.currentBoundaryType = newBoundaryType;

        // Update map layers
        this.updateBoundaryVisibility();
        this.updateActiveBoundaryData();
      }
    },
    
    /**
     * Check if bounds1 contains bounds2
     */
    boundsContainsBounds(bounds1, bounds2) {
      return bounds1.north >= bounds2.north &&
             bounds1.south <= bounds2.south &&
             bounds1.east >= bounds2.east &&
             bounds1.west <= bounds2.west;
    },
    
    /**
     * Convert locations to GeoJSON format for clustering (excluding depots)
     */
    convertLocationsToGeoJSON() {
      const features = [];
      
      this.dbLocations.forEach(loc => {
        // Skip depot locations as they will be handled separately
        if (loc.type === 'depot') {
          return;
        }
        
        // Validate coordinates before processing
        if (!loc.coordinates || !Array.isArray(loc.coordinates) || loc.coordinates.length < 2) {
          console.warn('Invalid coordinates for location:', loc.name, loc.coordinates);
          return;
        }
        
        // Ensure coordinates are numbers
        const lng = parseFloat(loc.coordinates[0]);
        const lat = parseFloat(loc.coordinates[1]);
        
        if (isNaN(lng) || isNaN(lat)) {
          console.warn('Invalid coordinate values for location:', loc.name, loc.coordinates);
          return;
        }
        
        const feature = {
          type: 'Feature',
          properties: {
            id: loc.id || loc._id,
            name: loc.name,
            type: loc.type,
            address: loc.address,
            demand: loc.demand,
            capacity: loc.capacity,
            vehicleCapability: loc.vehicleCapability,
            timeWindowStart: loc.timeWindowStart,
            timeWindowEnd: loc.timeWindowEnd,
            selected: this.selectedLocations.some(selected => (selected.id || selected._id) === (loc.id || loc._id))
          },
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          }
        };
        
        features.push(feature);
      });
      
      return {
        type: 'FeatureCollection',
        features: features
      };
    },
    
    /**
     * Convert depot locations to GeoJSON format for always-visible depot layer
     */
    convertDepotsToGeoJSON() {
      const features = [];
      
      this.dbLocations.forEach(loc => {
        // Only include depot locations
        if (loc.type !== 'depot') {
          return;
        }
        
        // Validate coordinates before processing
        if (!loc.coordinates || !Array.isArray(loc.coordinates) || loc.coordinates.length < 2) {
          console.warn('Invalid coordinates for depot:', loc.name, loc.coordinates);
          return;
        }
        
        // Ensure coordinates are numbers
        const lng = parseFloat(loc.coordinates[0]);
        const lat = parseFloat(loc.coordinates[1]);
        
        if (isNaN(lng) || isNaN(lat)) {
          console.warn('Invalid coordinate values for depot:', loc.name, loc.coordinates);
          return;
        }
        
        const feature = {
          type: 'Feature',
          properties: {
            id: loc.id || loc._id,
            name: loc.name,
            type: loc.type,
            address: loc.address,
            capacity: loc.capacity,
            selected: this.selectedLocations.some(selected => (selected.id || selected._id) === (loc.id || loc._id))
          },
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          }
        };
        
        features.push(feature);
      });
      
      return {
        type: 'FeatureCollection',
        features: features
      };
    },
    
    /**
     * Add clustered layers for location rendering
     */
    addClusteredLayers() {
      // Clusters layer
      this.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'locations',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#3498db',  // Blue for small clusters (2-9 points)
            10,
            '#e67e22',  // Orange for medium clusters (10-29 points)
            30,
            '#e74c3c',  // Red for large clusters (30-99 points)
            100,
            '#8e44ad'   // Purple for very large clusters (100+ points)
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            15,         // Small clusters
            10,
            25,         // Medium clusters
            30,
            35,         // Large clusters
            100,
            45          // Very large clusters
          ],
          'circle-opacity': 0.85,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-opacity': 1
        }
      });
      
      // Cluster count labels
      this.map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'locations',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        },
        paint: {
          'text-color': '#ffffff'
        }
      });
      
      // Individual location points
      this.map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'locations',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'case',
            ['==', ['get', 'type'], 'depot'],
            '#e74c3c',
            '#2ecc71'
          ],
          'circle-radius': [
            'case',
            ['==', ['get', 'type'], 'depot'],
            10,
            8
          ],
          'circle-stroke-width': [
            'case',
            ['get', 'selected'],
            3,
            2
          ],
          'circle-stroke-color': [
            'case',
            ['get', 'selected'],
            '#3498db',
            '#ffffff'
          ],
          'circle-opacity': 0.8
        }
      });
      
      // Add click handlers for clusters and points
      this.addClusterClickHandlers();
    },
    
    /**
     * Add depot layer for always-visible depot locations with warehouse icon
     */
    addDepotLayer() {
      // Depot warehouse icons (using symbol layer for better icon support)
      this.map.addLayer({
        id: 'depot-symbols',
        type: 'symbol',
        source: 'depots',
        layout: {
          'icon-image': 'depot-home-icon',
          'icon-size': 0.06,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-field': ['get', 'name'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 1],
          'text-max-width': 10
        },
        paint: {
          'text-color': '#2c3e50',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
          'icon-opacity': 1
        }
      });
      
      // Add backup depot circles in case home icon is not available
      this.map.addLayer({
        id: 'depot-circles',
        type: 'circle',
        source: 'depots',
        layout: {
          'visibility': 'none' // Initially hidden, will be shown if icon fails to load
        },
        paint: {
          'circle-color': '#e74c3c',
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            5, 8,
            10, 12,
            15, 16
          ],
          'circle-stroke-width': [
            'case',
            ['get', 'selected'],
            4,
            3
          ],
          'circle-stroke-color': [
            'case',
            ['get', 'selected'],
            '#3498db',
            '#ffffff'
          ],
          'circle-opacity': 0.9
        }
      });
      
      // Load home icon for depots
      this.loadDepotIcon();
      
      // Add click handlers for depot layer
      this.addDepotClickHandlers();
    },
    
    /**
     * Load home icon for depot markers
     */
    loadDepotIcon() {
      // Load the home icon from assets
      const img = new Image();
      img.onload = () => {
        if (!this.map.hasImage('depot-home-icon')) {
          this.map.addImage('depot-home-icon', img);
        }
      };
      img.onerror = () => {
        console.warn('Failed to load depot home icon, falling back to circle markers');
        // Show circle markers as fallback
        if (this.map.getLayer('depot-circles')) {
          this.map.setLayoutProperty('depot-circles', 'visibility', 'visible');
        }
      };
      
      // Use the home icon from assets
      img.src = require('@/assets/home-icon.png');
    },
    
    /**
     * Add click handlers for depot layer
     */
    addDepotClickHandlers() {
      // Depot symbol click handler
      this.map.on('click', 'depot-symbols', (e) => {
        const feature = e.features[0];
        const depot = this.dbLocations.find(loc => 
          (loc.id || loc._id) === feature.properties.id
        );
        
        if (depot) {
          this.toggleLocationSelection(depot);
          
          // Show popup
          const popupContent = this.createLocationPopupContent(depot);
          this.showPopup(e.lngLat, popupContent);
        }
      });
      
      // Depot circle click handler (backup)
      this.map.on('click', 'depot-circles', (e) => {
        const feature = e.features[0];
        const depot = this.dbLocations.find(loc => 
          (loc.id || loc._id) === feature.properties.id
        );
        
        if (depot) {
          this.toggleLocationSelection(depot);
          
          // Show popup
          const popupContent = this.createLocationPopupContent(depot);
          this.showPopup(e.lngLat, popupContent);
        }
      });
      
      // Mouse cursor on hover
      this.map.on('mouseenter', 'depot-symbols', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', 'depot-symbols', () => {
        this.map.getCanvas().style.cursor = '';
      });
      
      this.map.on('mouseenter', 'depot-circles', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', 'depot-circles', () => {
        this.map.getCanvas().style.cursor = '';
      });
    },

    /**
     * Load district boundaries from the API
     */
    async loadBoundaries() {
      try {
        console.log('Loading boundaries...');
        const response = await BoundariesService.getBoundaries();
        this.boundaries = response.data.data || [];
        console.log(`Loaded ${this.boundaries.length} boundaries`);
        console.log('Sample boundary:', this.boundaries[0]);
        
        // Update boundaries on map immediately if map is ready
        if (this.map && this.map.getSource('boundaries')) {
          console.log('Map and boundaries source ready, updating now');
          this.updateBoundariesOnMap();
        } else {
          console.log('Map or boundaries source not ready yet');
        }
      } catch (error) {
        console.error('Error loading boundaries:', error);
        console.error('Error details:', error.response?.data || error.message);
      }
    },

    /**
     * Load neighborhood boundaries from the API
     */
    async loadNeighborhoods() {
      try {
        console.log('Loading neighborhoods...');
        const response = await BoundariesService.getNeighborhoods();
        this.neighborhoods = response.data.data || [];
        console.log(`Loaded ${this.neighborhoods.length} neighborhoods`);
        console.log('Sample neighborhood:', this.neighborhoods[0]);
        
        // Update neighborhoods on map immediately if map is ready
        if (this.map && this.map.getSource('neighborhoods')) {
          console.log('Map and neighborhoods source ready, updating now');
          this.updateNeighborhoodsOnMap();
        } else {
          console.log('Map or neighborhoods source not ready yet');
        }
      } catch (error) {
        console.error('Error loading neighborhoods:', error);
        console.error('Error details:', error.response?.data || error.message);
      }
    },

    /**
     * Add boundary layers to the map
     */
    addBoundaryLayers() {
      // Boundary fill layer (soft, subtle)
      this.map.addLayer({
        id: 'boundaries-fill',
        type: 'fill',
        source: 'boundaries',
        layout: {
          'visibility': 'none'  // Start hidden, only show in boundary selection mode
        },
        paint: {
          'fill-color': [
            'case',
            ['==', ['get', 'selected'], true],
            'rgba(231, 76, 60, 0.4)',   // Red with transparency when selected
            'rgba(149, 165, 166, 0.15)' // Light gray when not selected
          ],
          'fill-opacity': 1
        }
      });
      
      // Boundary stroke layer (subtle outline)
      this.map.addLayer({
        id: 'boundaries-stroke',
        type: 'line',
        source: 'boundaries',
        layout: {
          'visibility': 'none'  // Start hidden, only show in boundary selection mode
        },
        paint: {
          'line-color': [
            'case',
            ['==', ['get', 'selected'], true],
            '#e74c3c',    // Red when selected
            '#95a5a6'     // Light gray when not selected
          ],
          'line-width': [
            'case',
            ['==', ['get', 'selected'], true],
            2,            // Thicker when selected
            1             // Thin when not selected
          ],
          'line-opacity': 0.7
        }
      });
      
      // Add hover effects for boundaries
      this.map.on('mouseenter', 'boundaries-fill', () => {
        if (this.selectionMode === 'boundary') {
          this.map.getCanvas().style.cursor = 'pointer';
        }
      });
      this.map.on('mouseleave', 'boundaries-fill', () => {
        this.map.getCanvas().style.cursor = '';
      });

      // Neighborhood fill layer (similar styling to boundaries)
      this.map.addLayer({
        id: 'neighborhoods-fill',
        type: 'fill',
        source: 'neighborhoods',
        layout: {
          'visibility': 'none'  // Start hidden, only show in boundary selection mode at high zoom
        },
        paint: {
          'fill-color': [
            'case',
            ['==', ['get', 'selected'], true],
            'rgba(231, 76, 60, 0.4)',   // Red with transparency when selected
            'rgba(149, 165, 166, 0.15)' // Light gray when not selected
          ],
          'fill-opacity': 1
        }
      });
      
      // Neighborhood stroke layer (similar styling to boundaries)
      this.map.addLayer({
        id: 'neighborhoods-stroke',
        type: 'line',
        source: 'neighborhoods',
        layout: {
          'visibility': 'none'  // Start hidden, only show in boundary selection mode at high zoom
        },
        paint: {
          'line-color': [
            'case',
            ['==', ['get', 'selected'], true],
            '#e74c3c',    // Red when selected
            '#95a5a6'     // Light gray when not selected
          ],
          'line-width': [
            'case',
            ['==', ['get', 'selected'], true],
            2,            // Thicker when selected
            1             // Thin when not selected
          ],
          'line-opacity': 0.7
        }
      });
      
      // Add hover effects for neighborhoods
      this.map.on('mouseenter', 'neighborhoods-fill', () => {
        if (this.selectionMode === 'boundary') {
          this.map.getCanvas().style.cursor = 'pointer';
        }
      });
      this.map.on('mouseleave', 'neighborhoods-fill', () => {
        this.map.getCanvas().style.cursor = '';
      });
    },

    /**
     * Update boundaries on map with data
     */
    updateBoundariesOnMap() {
      console.log('updateBoundariesOnMap called');
      console.log('Map exists:', !!this.map);
      console.log('Boundaries exist:', !!this.boundaries);
      console.log('Boundaries length:', this.boundaries?.length);
      
      if (!this.map || !this.boundaries) {
        console.log('Cannot update boundaries - missing map or boundaries');
        return;
      }
      
      const features = this.boundaries.map(boundary => ({
        ...boundary,
        properties: {
          ...boundary.properties,
          id: boundary.id,  // Ensure ID is in properties for click detection
          selected: this.selectedBoundaries.some(sb => sb.id === boundary.id)
        }
      }));
      
      console.log('Updating boundaries source with', features.length, 'features');
      console.log('Sample feature:', features[0]);
      
      if (this.map.getSource('boundaries')) {
        this.map.getSource('boundaries').setData({
          type: 'FeatureCollection',
          features: features
        });
        console.log('Boundaries data set successfully');
      } else {
        console.log('Boundaries source not found');
      }
    },

    /**
     * Update neighborhoods on map with data
     */
    updateNeighborhoodsOnMap() {
      console.log('updateNeighborhoodsOnMap called');
      console.log('Map exists:', !!this.map);
      console.log('Neighborhoods exist:', !!this.neighborhoods);
      console.log('Neighborhoods length:', this.neighborhoods?.length);
      
      if (!this.map || !this.neighborhoods) {
        console.log('Cannot update neighborhoods - missing map or neighborhoods');
        return;
      }
      
      const features = this.neighborhoods.map(neighborhood => ({
        ...neighborhood,
        properties: {
          ...neighborhood.properties,
          id: neighborhood.id,  // Ensure ID is in properties for click detection
          selected: this.selectedBoundaries.some(sb => sb.id === neighborhood.id)
        }
      }));
      
      console.log('Updating neighborhoods source with', features.length, 'features');
      console.log('Sample feature:', features[0]);
      
      if (this.map.getSource('neighborhoods')) {
        this.map.getSource('neighborhoods').setData({
          type: 'FeatureCollection',
          features: features
        });
        console.log('Neighborhoods data set successfully');
      } else {
        console.log('Neighborhoods source not found');
      }
    },

    /**
     * Update the active boundary data based on current boundary type
     */
    updateActiveBoundaryData() {
      if (this.currentBoundaryType === 'neighborhoods') {
        this.updateNeighborhoodsOnMap();
      } else {
        this.updateBoundariesOnMap();
      }
    },

    /**
     * Handle boundary click - select boundary and filter locations
     */
    handleBoundaryClick(e) {
      console.log('Boundary click event:', e);
      console.log('Current selection mode:', this.selectionMode);
      
      // Only handle boundary clicks when in boundary selection mode
      if (this.selectionMode !== 'boundary') {
        console.log('Not in boundary selection mode, ignoring click');
        return;
      }
      
      if (e.features.length > 0) {
        const clickedBoundary = e.features[0];
        // Get ID from properties (we ensure it's there in updateBoundariesOnMap)
        const boundaryId = clickedBoundary.properties.id;
        console.log('Clicked boundary ID:', boundaryId);
        console.log('Clicked boundary properties:', clickedBoundary.properties);
        console.log('Full clicked boundary:', clickedBoundary);
        
        // Check if Ctrl key is pressed for multi-select
        const isCtrlPressed = e.originalEvent && (e.originalEvent.ctrlKey || e.originalEvent.metaKey);
        console.log('Ctrl pressed:', isCtrlPressed);
        
        // Find boundary in the appropriate array based on current type
        const boundaryArray = this.currentBoundaryType === 'neighborhoods' 
          ? this.neighborhoods 
          : this.boundaries;
        const clickedBoundaryObj = boundaryArray.find(b => b.id === boundaryId);
        
        if (isCtrlPressed) {
          // Multi-select mode: add/remove from selection
          const existingIndex = this.selectedBoundaries.findIndex(b => b.id === boundaryId);
          if (existingIndex >= 0) {
            // Remove from selection
            console.log(`Removing ${this.currentBoundaryType.slice(0, -1)} from selection:`, boundaryId);
            this.selectedBoundaries.splice(existingIndex, 1);
          } else {
            // Add to selection
            console.log(`Adding ${this.currentBoundaryType.slice(0, -1)} to selection:`, boundaryId);
            this.selectedBoundaries.push(clickedBoundaryObj);
          }
        } else {
          // Single select mode: replace selection
          const isAlreadySelected = this.selectedBoundaries.length === 1 && this.selectedBoundaries[0].id === boundaryId;
          if (isAlreadySelected) {
            // Deselect if clicking the same boundary
            console.log(`Deselecting ${this.currentBoundaryType.slice(0, -1)}:`, boundaryId);
            this.selectedBoundaries = [];
          } else {
            // Select only this boundary
            console.log(`Selecting ${this.currentBoundaryType.slice(0, -1)}:`, boundaryId);
            this.selectedBoundaries = [clickedBoundaryObj];
          }
        }
        
        console.log('Selected boundaries:', this.selectedBoundaries);
        
        // Update boundary appearance based on current type
        this.updateActiveBoundaryData();
        
        // Filter locations by boundaries
        this.filterLocationsByBoundary();
        
        // Emit event for parent components
        this.$emit('boundary-selected', this.selectedBoundaries);
      } else {
        console.log('No features found in boundary click');
      }
    },

    /**
     * Filter locations that fall within the selected boundaries
     */
    filterLocationsByBoundary() {
      if (!this.selectedBoundaries || this.selectedBoundaries.length === 0) {
        // Show all locations when no boundaries are selected
        this.$emit('locations-filtered', null);
        return;
      }
      
      // Find locations that fall within ANY of the selected boundaries
      const locationsInBoundaries = this.dbLocations.filter(location => {
        if (!location.coordinates) {
          return false;
        }
        
        const point = turf.point(location.coordinates);
        
        // Check if location is within any of the selected boundaries
        return this.selectedBoundaries.some(boundary => {
          return turf.booleanPointInPolygon(point, boundary.geometry);
        });
      });
      
      const boundaryNames = this.selectedBoundaries.map(b => 
        b.properties?.NAME_2 || b.properties?.NAME_1 || 'Unknown'
      ).join(', ');
      
      console.log(`Found ${locationsInBoundaries.length} locations in ${this.selectedBoundaries.length} selected boundaries: ${boundaryNames}`);
      this.$emit('locations-filtered', locationsInBoundaries);
    },
    
    /**
     * Add markers for DB locations (fallback for small datasets, excluding depots)
     */
    addDbMarkers() {
      // Remove existing DB markers
      this.dbMarkers.forEach(marker => marker.remove());
      this.dbMarkers = [];
      
      // Add a marker for each non-depot DB location
      this.dbLocations.forEach(loc => {
        // Skip depot locations as they are handled by the depot layer
        if (loc.type === 'depot') {
          return;
        }
        // Validate coordinates before creating marker
        if (!loc.coordinates || !Array.isArray(loc.coordinates) || loc.coordinates.length < 2) {
          console.warn('Invalid coordinates for location:', loc.name, loc.coordinates);
          return;
        }
        
        // Ensure coordinates are numbers
        const lng = parseFloat(loc.coordinates[0]);
        const lat = parseFloat(loc.coordinates[1]);
        
        if (isNaN(lng) || isNaN(lat)) {
          console.warn('Invalid coordinate values for location:', loc.name, loc.coordinates);
          return;
        }
        
        const el = document.createElement('div');
        el.className = loc.type === 'depot' ? 'depot-db-marker' : 'demand-db-marker';
        el.title = loc.name;
        el.setAttribute('data-location-id', loc.id || loc._id);
        
        // Add selection state tracking
        if (this.selectedLocations.some(selected => (selected.id || selected._id) === (loc.id || loc._id))) {
          el.classList.add('selected-location-marker');
        }
        
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lng, lat])
          .addTo(this.map);
        
        // Add click event listener for individual location selection and popup
        el.addEventListener('click', (event) => {
          event.stopPropagation(); // Prevent map click from firing
          this.toggleLocationSelection(loc);
          
          // Show popup
          const popupContent = this.createLocationPopupContent(loc);
          this.showPopup([lng, lat], popupContent);
        });
        
        // Store reference to location data for animation
        marker._locationData = loc;
        this.dbMarkers.push(marker);
      });
      
      // Also update depot source when using marker mode for consistency
      const depotGeoJsonData = this.convertDepotsToGeoJSON();
      this.map.getSource('depots').setData(depotGeoJsonData);
    },
    
    /**
     * Add click handlers for clusters and individual points
     */
    addClusterClickHandlers() {
      // Cluster click handler - zoom in to expand cluster
      this.map.on('click', 'clusters', (e) => {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: ['clusters']
        });
        const clusterId = features[0].properties.cluster_id;
        this.map.getSource('locations').getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err) return;
            
            this.map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom
            });
          }
        );
      });
      
      // Individual point click handler
      this.map.on('click', 'unclustered-point', (e) => {
        const feature = e.features[0];
        const location = this.dbLocations.find(loc => 
          (loc.id || loc._id) === feature.properties.id
        );
        
        if (location) {
          this.toggleLocationSelection(location);
          
          // Show popup
          const popupContent = this.createLocationPopupContent(location);
          this.showPopup(e.lngLat, popupContent);
        }
      });
      
      // Mouse cursor on hover
      this.map.on('mouseenter', 'clusters', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', 'clusters', () => {
        this.map.getCanvas().style.cursor = '';
      });
      
      this.map.on('mouseenter', 'unclustered-point', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', 'unclustered-point', () => {
        this.map.getCanvas().style.cursor = '';
      });
    },
    
    /**
     * Render locations using appropriate method based on count
     */
    renderLocations() {
      const endTiming = PerformanceMonitor.startTiming('locationRendering');
      
      if (!this.showLocations || this.dbLocations.length === 0) {
        this.clearLocationRendering();
        endTiming({ 
          locationCount: 0, 
          renderingMode: 'none' 
        });
        return;
      }
      
      // Determine rendering mode based on manual override or location count
      if (this.forceClusteringMode !== null) {
        this.useClusteredRendering = this.forceClusteringMode;
      } else {
        this.useClusteredRendering = this.dbLocations.length > this.locationThreshold;
      }
      
      if (this.useClusteredRendering) {
        // Use clustered rendering for large datasets
        this.renderClusteredLocations();
        endTiming({ 
          locationCount: this.dbLocations.length, 
          renderingMode: 'clustered',
          threshold: this.locationThreshold,
          manualOverride: this.forceClusteringMode
        });
      } else {
        // Use individual markers for small datasets
        this.addDbMarkers();
        endTiming({ 
          locationCount: this.dbLocations.length, 
          renderingMode: 'markers',
          threshold: this.locationThreshold,
          manualOverride: this.forceClusteringMode
        });
      }
    },
    
    /**
     * Render locations using clustered approach
     */
    renderClusteredLocations() {
      const endTiming = PerformanceMonitor.startTiming('clusteringPerformance');
      
      // Clear any existing markers
      this.dbMarkers.forEach(marker => marker.remove());
      this.dbMarkers = [];
      
      // Convert demand locations to GeoJSON and update clustered source
      const geoJsonData = this.convertLocationsToGeoJSON();
      this.map.getSource('locations').setData(geoJsonData);
      
      // Convert depot locations to GeoJSON and update depot source
      const depotGeoJsonData = this.convertDepotsToGeoJSON();
      this.map.getSource('depots').setData(depotGeoJsonData);
      
      endTiming({
        locationCount: this.dbLocations.length,
        demandFeaturesGenerated: geoJsonData.features.length,
        depotFeaturesGenerated: depotGeoJsonData.features.length
      });
    },
    
    /**
     * Clear all location rendering (markers and layers)
     */
    clearLocationRendering() {
      // Clear markers
      this.dbMarkers.forEach(marker => marker.remove());
      this.dbMarkers = [];
      
      // Clear clustered data
      if (this.map.getSource('locations')) {
        this.map.getSource('locations').setData({
          type: 'FeatureCollection',
          features: []
        });
      }
      
      // Clear depot data
      if (this.map.getSource('depots')) {
        this.map.getSource('depots').setData({
          type: 'FeatureCollection',
          features: []
        });
      }
    },
    
    /**
     * Update location selection state in clustered rendering
     */
    updateClusteredSelectionState() {
      if (this.useClusteredRendering && this.map.getSource('locations')) {
        const geoJsonData = this.convertLocationsToGeoJSON();
        this.map.getSource('locations').setData(geoJsonData);
        
        // Also update depot selection state
        const depotGeoJsonData = this.convertDepotsToGeoJSON();
        this.map.getSource('depots').setData(depotGeoJsonData);
      }
    },
    
    /**
     * Remove a specific marker
     */
    removeMarker(index) {
      if (index >= 0 && index < this.markers.length) {
        this.markers[index].remove();
        this.markers.splice(index, 1);
      }
    },
    
    /**
     * Show loading indicator
     */
    showLoadingIndicator() {
      if (this.$refs.loadingIndicator) {
        this.$refs.loadingIndicator.classList.add('visible');
      }
    },

    /**
     * Hide loading indicator
     */
    hideLoadingIndicator() {
      if (this.$refs.loadingIndicator) {
        this.$refs.loadingIndicator.classList.remove('visible');
      }
    },    /**
     * Display solution routes on the map
     */
    async displayRoutes(solution, allLocations, routeColors) {
      // Clear previous routes
      this.clearRoutes();
      
      // Check if solution is null or invalid
      if (!solution || !solution.routes) {
        this.hideLoadingIndicator();
        return;
      }
      
      // Show loading indicator
      this.showLoadingIndicator();
      
      if (!this.map.getSource('routes')) {
        console.error('Routes source not found');
        this.hideLoadingIndicator();
        return;
      }
      
      // Create an array to store all route features
      let allFeatures = [];
      
      // Process each route
      for (let routeIndex = 0; routeIndex < solution.routes.length; routeIndex++) {
        const route = solution.routes[routeIndex];
        const routeStops = route.stops;
        
        // Create a feature for each segment of the route
        for (let i = 0; i < routeStops.length - 1; i++) {
          const startPoint = allLocations[routeStops[i]];
          const endPoint = allLocations[routeStops[i + 1]];
          
          // Get the detailed route geometry between these points
          const routeGeometry = await this.getRouteGeometry(startPoint, endPoint);
          
          // Create a feature for this segment
          const feature = {
            type: 'Feature',
            properties: {
              routeId: routeIndex,
              color: routeColors[routeIndex]
            },
            geometry: {
              type: 'LineString',
              coordinates: routeGeometry
            }
          };
          
          allFeatures.push(feature);
        }
      }
        // Update the route source data with all features
      this.map.getSource('routes').setData({
        type: 'FeatureCollection',
        features: allFeatures
      });
      
      // Hide loading indicator after all routes are calculated
      this.hideLoadingIndicator();
      
      // Add layers for each route
      solution.routes.forEach((route, routeIndex) => {
        const color = routeColors[routeIndex];
        const layerId = `route-${routeIndex}`;
        
        // Remove if exists
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
        
        // Add line layer for this route
        this.map.addLayer({
          id: layerId,
          type: 'line',
          source: 'routes',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': color,
            'line-width': 4,
            'line-opacity': 0.8
          },
          filter: ['==', ['get', 'routeId'], routeIndex]
        });
        
        this.routeLayers.push(layerId);
      });
        // Fit map to show all the routes
      this.fitToRoutes(allLocations);
      
      // Hide loading indicator when routes are displayed
      this.hideLoadingIndicator();
    },
    
    /**
     * Clear route layers
     */
    clearRoutes() {
      // Remove all route layers
      this.routeLayers.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      });
      this.routeLayers = [];
      
      // Reset the route source data if it exists
      if (this.map.getSource('routes')) {
        this.map.getSource('routes').setData({
          type: 'FeatureCollection',
          features: []
        });
      }
      
      // Clear LAP-specific sources and layers
      if (this.map.getSource('lap-facilities')) {
        this.map.getSource('lap-facilities').setData({
          type: 'FeatureCollection',
          features: []
        });
      }
      
      if (this.map.getSource('lap-demands')) {
        this.map.getSource('lap-demands').setData({
          type: 'FeatureCollection',
          features: []
        });
      }
      
      // Remove LAP layers if they exist
      if (this.map.getLayer('lap-facilities-layer')) {
        this.map.removeLayer('lap-facilities-layer');
      }
      
      if (this.map.getLayer('lap-demands-layer')) {
        this.map.removeLayer('lap-demands-layer');
      }
    },
    
    /**
     * Display LAP allocation results with straight lines from demands to facilities
     */
    async displayLAPAllocation(solution) {
      console.log('Displaying LAP allocation:', solution);
      
      // Clear previous routes/allocations
      this.clearRoutes();
      
      if (!solution || !solution.assignments || !solution.facilities || !solution.demands) {
        console.error('Invalid LAP solution data');
        return;
      }
      
      // Show loading indicator
      this.$refs.loadingIndicator.style.display = 'block';
      
      try {
        // Define facility colors
        const facilityColors = [
          '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
          '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF6348',
          '#2ED573', '#A55EEA'
        ];
        
        // Create allocation line features
        const allocationFeatures = [];
        const facilityMarkerFeatures = [];
        const demandMarkerFeatures = [];
        
        // Add facility markers
        solution.facilities.forEach((facility, facilityIndex) => {
          const color = facilityColors[facilityIndex % facilityColors.length];
          
          facilityMarkerFeatures.push({
            type: 'Feature',
            properties: {
              type: 'facility',
              facilityId: facility.id,
              facilityIndex: facilityIndex,
              name: facility.name,
              capacity: facility.capacity,
              color: color,
              utilization: solution.facility_utilization && solution.facility_utilization[facilityIndex] 
                ? solution.facility_utilization[facilityIndex].utilization_rate 
                : 0
            },
            geometry: {
              type: 'Point',
              coordinates: facility.coordinates
            }
          });
        });
        
        // Create allocation lines and demand markers
        solution.assignments.forEach((assignment) => {
          const facility = solution.facilities[assignment.facility_idx];
          const demand = solution.demands[assignment.demand_idx];
          const color = facilityColors[assignment.facility_idx % facilityColors.length];
          
          // Add allocation line
          allocationFeatures.push({
            type: 'Feature',
            properties: {
              type: 'allocation',
              facilityId: assignment.facility_idx,
              demandId: assignment.demand_idx,
              color: color,
              distance: assignment.distance,
              cost: assignment.cost
            },
            geometry: {
              type: 'LineString',
              coordinates: [facility.coordinates, demand.coordinates]
            }
          });
          
          // Add demand marker
          demandMarkerFeatures.push({
            type: 'Feature',
            properties: {
              type: 'demand',
              demandId: demand.id,
              demandIndex: assignment.demand_idx,
              name: demand.name,
              demand: demand.demand,
              assignedFacility: assignment.facility_idx,
              color: color
            },
            geometry: {
              type: 'Point',
              coordinates: demand.coordinates
            }
          });
        });
        
        // Update route source with allocation lines
        this.map.getSource('routes').setData({
          type: 'FeatureCollection',
          features: allocationFeatures
        });
        
        // Add or update facility markers source
        if (!this.map.getSource('lap-facilities')) {
          this.map.addSource('lap-facilities', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: facilityMarkerFeatures
            }
          });
        } else {
          this.map.getSource('lap-facilities').setData({
            type: 'FeatureCollection',
            features: facilityMarkerFeatures
          });
        }
        
        // Add or update demand markers source
        if (!this.map.getSource('lap-demands')) {
          this.map.addSource('lap-demands', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: demandMarkerFeatures
            }
          });
        } else {
          this.map.getSource('lap-demands').setData({
            type: 'FeatureCollection',
            features: demandMarkerFeatures
          });
        }
        
        // Add allocation line layers for each facility
        solution.facilities.forEach((facility, facilityIndex) => {
          const color = facilityColors[facilityIndex % facilityColors.length];
          const layerId = `allocation-${facilityIndex}`;
          
          // Remove layer if exists
          if (this.map.getLayer(layerId)) {
            this.map.removeLayer(layerId);
          }
          
          // Add line layer for this facility's allocations
          this.map.addLayer({
            id: layerId,
            type: 'line',
            source: 'routes',
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': color,
              'line-width': 3,
              'line-opacity': 0.7
            },
            filter: ['==', ['get', 'facilityId'], facilityIndex]
          });
          
          this.routeLayers.push(layerId);
        });
        
        // Add facility marker layer
        if (!this.map.getLayer('lap-facilities-layer')) {
          this.map.addLayer({
            id: 'lap-facilities-layer',
            type: 'circle',
            source: 'lap-facilities',
            paint: {
              'circle-radius': [
                'interpolate',
                ['linear'],
                ['get', 'utilization'],
                0, 12,
                1, 20
              ],
              'circle-color': ['get', 'color'],
              'circle-opacity': 0.8,
              'circle-stroke-width': 3,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-opacity': 1
            }
          });
          this.routeLayers.push('lap-facilities-layer');
        }
        
        // Add demand marker layer
        if (!this.map.getLayer('lap-demands-layer')) {
          this.map.addLayer({
            id: 'lap-demands-layer',
            type: 'circle',
            source: 'lap-demands',
            paint: {
              'circle-radius': 8,
              'circle-color': ['get', 'color'],
              'circle-opacity': 0.6,
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-opacity': 1
            }
          });
          this.routeLayers.push('lap-demands-layer');
        }
        
        // Add hover effects for facilities
        this.map.on('mouseenter', 'lap-facilities-layer', () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        
        this.map.on('mouseleave', 'lap-facilities-layer', () => {
          this.map.getCanvas().style.cursor = '';
        });
        
        // Add hover effects for demands
        this.map.on('mouseenter', 'lap-demands-layer', () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        
        this.map.on('mouseleave', 'lap-demands-layer', () => {
          this.map.getCanvas().style.cursor = '';
        });
        
        // Add popups for facility markers
        this.map.on('click', 'lap-facilities-layer', (e) => {
          const feature = e.features[0];
          const props = feature.properties;
          
          const popupContent = `
            <div class="lap-popup facility-popup">
              <h4>${props.name}</h4>
              <p><strong>Type:</strong> Facility</p>
              <p><strong>Capacity:</strong> ${props.capacity}</p>
              <p><strong>Utilization:</strong> ${(props.utilization * 100).toFixed(1)}%</p>
            </div>
          `;
          
          this.showPopup(e.lngLat, popupContent);
        });
        
        // Add popups for demand markers
        this.map.on('click', 'lap-demands-layer', (e) => {
          const feature = e.features[0];
          const props = feature.properties;
          
          const popupContent = `
            <div class="lap-popup demand-popup">
              <h4>${props.name}</h4>
              <p><strong>Type:</strong> Demand Point</p>
              <p><strong>Demand:</strong> ${props.demand} units</p>
              <p><strong>Assigned to:</strong> Facility ${props.assignedFacility + 1}</p>
            </div>
          `;
          
          this.showPopup(e.lngLat, popupContent);
        });
        
        // Fit map to show all features
        const allCoordinates = [
          ...solution.facilities.map(f => f.coordinates),
          ...solution.demands.map(d => d.coordinates)
        ];
        this.fitToRoutes(allCoordinates);
        
      } catch (error) {
        console.error('Error displaying LAP allocation:', error);
      } finally {
        // Hide loading indicator
        this.$refs.loadingIndicator.style.display = 'none';
      }
    },
    
    /**
     * Adjust map view to show all locations
     */
    fitToRoutes(allLocations) {
      if (allLocations.length === 0) return;
      
      // Create bounds that include all locations
      const bounds = new mapboxgl.LngLatBounds();
      
      // Add each location to the bounds
      allLocations.forEach(location => {
        bounds.extend([location[0], location[1]]);
      });
      
      // Fit the map to the bounds with padding
      this.map.fitBounds(bounds, {
        padding: 50
      });
    },

    /**
     * Fetch route geometry between two points using OSRM Directions API
     * @param {Array} start - [lng, lat] coordinates of start point
     * @param {Array} end - [lng, lat] coordinates of end point
     * @returns {Promise} - Promise resolving to route geometry
     */
    async getRouteGeometry(start, end) {
      const cacheKey = `${start.join(',')}-${end.join(',')}`;
      
      // Check if we already have this route in the cache
      if (this.directionsCache[cacheKey]) {
        return this.directionsCache[cacheKey];
      }
      
      try {
        // Format the request URL for OSRM Directions API
        const url = `http://router.project-osrm.org/route/v1/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.routes && data.routes.length > 0) {
          // Cache the result
          this.directionsCache[cacheKey] = data.routes[0].geometry.coordinates;
          return data.routes[0].geometry.coordinates;
        } else {
          console.warn('No route found between points:', start, end);
          // Fall back to direct line if no route found
          return [start, end];
        }
      } catch (error) {
        console.error('Error fetching route:', error);
        // Fall back to direct line in case of error
        return [start, end];
      }
    },
    
    /**
     * Handle draw create events (polygon selection)
     */
    handleDrawCreate(e) {
      e.features.forEach(feature => {
        this.selectLocationsInPolygon(feature.geometry);
      });
    },
    
    /**
     * Handle draw update events
     */
    handleDrawUpdate(e) {
      // Clear previous selections and reselect
      this.selectedLocations = [];
      e.features.forEach(feature => {
        this.selectLocationsInPolygon(feature.geometry);
      });
    },
    
    /**
     * Handle draw delete events
     */
    handleDrawDelete() {
      this.selectedLocations = [];
      this.$emit('locations-selected', []);
    },
    
    /**
     * Select locations within a drawn polygon
     */
    selectLocationsInPolygon(polygon) {
      const selectedLocs = [];
      
      this.dbLocations.forEach(location => {
        // Validate coordinates before processing
        if (!location.coordinates || !Array.isArray(location.coordinates) || location.coordinates.length < 2) {
          return;
        }
        
        const point = turf.point(location.coordinates);
        if (turf.booleanPointInPolygon(point, polygon)) {
          selectedLocs.push(location);
        }
      });
      
      this.selectedLocations = [...new Set([...this.selectedLocations, ...selectedLocs])];
      
      // Refresh markers to ensure proper positioning and state
      this.refreshSelectedMarkers();
      
      // Animate newly selected locations
      this.animateSelectedMarkers(selectedLocs);
    },
    
    /**
     * Refresh markers to maintain proper positioning and selection state
     */
    refreshSelectedMarkers() {
      if (this.useClusteredRendering) {
        // For clustered rendering, update the data source
        this.updateClusteredSelectionState();
      } else {
        // Update marker classes without recreating markers to prevent position issues
        this.dbMarkers.forEach(marker => {
          const locationData = marker._locationData;
          if (locationData) {
            const locationId = locationData.id || locationData._id;
            const markerElement = marker.getElement();
            const isSelected = this.selectedLocations.some(selected => (selected.id || selected._id) === locationId);
            
            if (isSelected) {
              markerElement.classList.add('selected-location-marker');
            } else {
              markerElement.classList.remove('selected-location-marker', 'selection-highlight', 'pulse-animation');
            }
          }
        });
      }
    },
    
    /**
     * Create detailed popup content for location markers
     */
    createLocationPopupContent(location) {
      // Format coordinates display
      const coordinates = location.coordinates || [];
      const coordsDisplay = coordinates.length >= 2 ? 
        `<p><strong>Coordinates:</strong> ${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}</p>` : '';
      
      // Format time window display for demand locations
      const timeWindowDisplay = location.type === 'demand' && 
        (location.timeWindowStart || location.timeWindowEnd) ? 
        `<p><strong>Time Window:</strong> ${this.formatTimeWindow(location.timeWindowStart, location.timeWindowEnd)}</p>` : '';
      
      return `
        <div class="location-popup">
          <h4 class="popup-title">${location.name || 'Location'}</h4>
          <div class="popup-details">
            <p><strong>Type:</strong> ${location.type || 'Unknown'}</p>
            ${coordsDisplay}
            ${location.address ? `<p><strong>Address:</strong> ${location.address}</p>` : ''}
            ${timeWindowDisplay}
            ${location.demand ? `<p><strong>Demand:</strong> ${location.demand}</p>` : ''}
            ${location.capacity ? `<p><strong>Capacity:</strong> ${location.capacity}</p>` : ''}
            ${location.vehicleCapability ? `<p><strong>Vehicle Capability:</strong> ${location.vehicleCapability}</p>` : ''}
          </div>
          <div class="popup-actions">
            <button class="popup-btn" onclick="window.navigateToLocation('${location._id || location.id}')">
              Go to Location Details
            </button>
          </div>
        </div>
      `;
    },
    
    /**
     * Create a custom rectangle drawing mode for Mapbox Draw
     */
    createRectangleMode() {
      const drawRectangle = { ...MapboxDraw.modes.draw_polygon };
      
      drawRectangle.clickAnywhere = function(state, e) {
        // Only allow creating rectangles with exactly 4 points
        if (state.currentVertexPosition === 0) {
          state.polygon.updateCoordinate(`0.0`, e.lngLat.lng, e.lngLat.lat);
          state.currentVertexPosition++;
        } else if (state.currentVertexPosition === 1) {
          // Calculate rectangle coordinates
          const startPoint = state.polygon.coordinates[0][0];
          const endPoint = [e.lngLat.lng, e.lngLat.lat];
          
          // Create rectangle coordinates
          const coordinates = [
            [startPoint[0], startPoint[1]], // top-left
            [endPoint[0], startPoint[1]],   // top-right
            [endPoint[0], endPoint[1]],     // bottom-right
            [startPoint[0], endPoint[1]],   // bottom-left
            [startPoint[0], startPoint[1]]  // close polygon
          ];
          
          // Update polygon with rectangle coordinates
          coordinates.forEach((coord, index) => {
            if (index < 4) {
              state.polygon.updateCoordinate(`0.${index}`, coord[0], coord[1]);
            }
          });
          state.polygon.updateCoordinate(`0.4`, coordinates[4][0], coordinates[4][1]);
          
          // Finish the rectangle
          return this.changeMode('simple_select', { featureIds: [state.polygon.id] });
        }
      };
      
      return drawRectangle;
    },
    
    /**
     * Add custom rectangle control button
     */
    addRectangleControl() {
      // Create rectangle button
      const rectangleButton = document.createElement('button');
      rectangleButton.className = 'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_rectangle';
      rectangleButton.title = 'Draw Rectangle';
      rectangleButton.innerHTML = '⬛';
      rectangleButton.addEventListener('click', () => {
        this.draw.changeMode('draw_rectangle');
      });
      
      // Find the draw control container and add the button
      setTimeout(() => {
        const drawControlGroup = document.querySelector('.mapbox-gl-draw_ctrl-draw-btn').parentNode;
        if (drawControlGroup) {
          drawControlGroup.insertBefore(rectangleButton, drawControlGroup.firstChild);
        }
      }, 100);
    },
    
    /**
     * Add clustering toggle control button
     */
    addClusteringToggleControl() {
      // Create custom control
      class ClusteringToggleControl {
        constructor(component) {
          this.component = component;
        }
        
        onAdd(map) {
          this.map = map;
          this.container = document.createElement('div');
          this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group clustering-toggle-control';
          
          // Auto mode button
          this.autoButton = document.createElement('button');
          this.autoButton.className = 'clustering-toggle-btn active';
          this.autoButton.title = 'Auto mode (automatic clustering based on data size)';
          this.autoButton.innerHTML = '🔄';
          this.autoButton.addEventListener('click', () => this.component.setClusteringMode(null));
          
          // Clustering mode button
          this.clusterButton = document.createElement('button');
          this.clusterButton.className = 'clustering-toggle-btn';
          this.clusterButton.title = 'Force clustering view';
          this.clusterButton.innerHTML = '🔵';
          this.clusterButton.addEventListener('click', () => this.component.setClusteringMode(true));
          
          // Points mode button
          this.pointsButton = document.createElement('button');
          this.pointsButton.className = 'clustering-toggle-btn';
          this.pointsButton.title = 'Force individual points view';
          this.pointsButton.innerHTML = '📍';
          this.pointsButton.addEventListener('click', () => this.component.setClusteringMode(false));
          
          this.container.appendChild(this.autoButton);
          this.container.appendChild(this.clusterButton);
          this.container.appendChild(this.pointsButton);
          
          return this.container;
        }
        
        onRemove() {
          this.container.parentNode.removeChild(this.container);
          this.map = undefined;
        }
        
        updateButtons(mode) {
          // Remove active class from all buttons
          [this.autoButton, this.clusterButton, this.pointsButton].forEach(btn => {
            btn.classList.remove('active');
          });
          
          // Add active class to current mode
          if (mode === null) {
            this.autoButton.classList.add('active');
          } else if (mode === true) {
            this.clusterButton.classList.add('active');
          } else if (mode === false) {
            this.pointsButton.classList.add('active');
          }
        }
      }
      
      this.clusterToggleControl = new ClusteringToggleControl(this);
      this.map.addControl(this.clusterToggleControl, 'top-right');
    },
    
    /**
     * Add boundary selection control
     */
    addBoundarySelectionControl() {
      // Create custom control
      class BoundarySelectionControl {
        constructor(component) {
          this.component = component;
        }
        
        onAdd(map) {
          this.map = map;
          this.container = document.createElement('div');
          this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group boundary-selection-control';
          
          // Normal selection mode button
          this.normalButton = document.createElement('button');
          this.normalButton.className = 'boundary-selection-btn active';
          this.normalButton.title = 'Normal mode - click to add locations';
          this.normalButton.innerHTML = '👆';
          this.normalButton.addEventListener('click', () => this.component.setSelectionMode('normal'));
          
          // Polygon selection mode button
          this.polygonButton = document.createElement('button');
          this.polygonButton.className = 'boundary-selection-btn';
          this.polygonButton.title = 'Polygon selection - draw polygons to select locations';
          this.polygonButton.innerHTML = '📐';
          this.polygonButton.addEventListener('click', () => this.component.setSelectionMode('polygon'));
          
          // Boundary selection mode button
          this.boundaryButton = document.createElement('button');
          this.boundaryButton.className = 'boundary-selection-btn';
          this.boundaryButton.title = 'Boundary selection - click districts/neighborhoods to select locations (Ctrl+click for multi-select)';
          this.boundaryButton.innerHTML = '🗺️';
          this.boundaryButton.addEventListener('click', () => this.component.setSelectionMode('boundary'));
          
          this.container.appendChild(this.normalButton);
          this.container.appendChild(this.polygonButton);
          this.container.appendChild(this.boundaryButton);
          
          return this.container;
        }
        
        onRemove() {
          this.container.parentNode.removeChild(this.container);
          this.map = undefined;
        }
        
        updateButtons(mode) {
          // Remove active class from all buttons
          this.normalButton.classList.remove('active');
          this.polygonButton.classList.remove('active');
          this.boundaryButton.classList.remove('active');
          
          // Add active class to current mode button
          switch(mode) {
            case 'normal':
              this.normalButton.classList.add('active');
              break;
            case 'polygon':
              this.polygonButton.classList.add('active');
              break;
            case 'boundary':
              this.boundaryButton.classList.add('active');
              break;
          }
        }
      }
      
      // Add the control to the map
      this.boundarySelectionControl = new BoundarySelectionControl(this);
      this.map.addControl(this.boundarySelectionControl, 'top-left');
    },
    
    /**
     * Set selection mode
     */
    setSelectionMode(mode) {
      console.log('Setting selection mode to:', mode);
      this.selectionMode = mode;
      
      // Update button states
      if (this.boundarySelectionControl) {
        this.boundarySelectionControl.updateButtons(mode);
      }
      
      // Update draw control visibility based on mode
      if (mode === 'polygon') {
        // Enable polygon drawing mode
        this.draw.changeMode('simple_select');
      } else if (mode === 'boundary') {
        // Disable draw mode when in boundary selection
        this.draw.changeMode('simple_select');
        
        // Set boundary type based on current zoom level
        if (this.map) {
          const currentZoom = this.map.getZoom();
          this.currentBoundaryType = currentZoom >= this.zoomThreshold ? 'neighborhoods' : 'districts';
          console.log(`Setting boundary type to ${this.currentBoundaryType} based on zoom ${currentZoom}`);
        }
        
        // Ensure appropriate boundaries are loaded and displayed
        this.updateActiveBoundaryData();
      } else {
        // Normal mode
        this.draw.changeMode('simple_select');
      }
      
      // Update boundary visibility
      this.updateBoundaryVisibility();
    },
    
    /**
     * Update boundary layer visibility based on selection mode
     */
    updateBoundaryVisibility() {
      if (!this.map) return;
      
      const showBoundaries = (this.selectionMode === 'boundary');
      console.log('Updating boundary visibility, show boundaries:', showBoundaries);
      console.log('Current boundary type:', this.currentBoundaryType);
      
      // District boundaries visibility
      const districtVisibility = (showBoundaries && this.currentBoundaryType === 'districts') ? 'visible' : 'none';
      if (this.map.getLayer('boundaries-fill')) {
        this.map.setLayoutProperty('boundaries-fill', 'visibility', districtVisibility);
        console.log('Set boundaries-fill visibility to:', districtVisibility);
      }
      if (this.map.getLayer('boundaries-stroke')) {
        this.map.setLayoutProperty('boundaries-stroke', 'visibility', districtVisibility);
        console.log('Set boundaries-stroke visibility to:', districtVisibility);
      }
      
      // Neighborhood boundaries visibility
      const neighborhoodVisibility = (showBoundaries && this.currentBoundaryType === 'neighborhoods') ? 'visible' : 'none';
      if (this.map.getLayer('neighborhoods-fill')) {
        this.map.setLayoutProperty('neighborhoods-fill', 'visibility', neighborhoodVisibility);
        console.log('Set neighborhoods-fill visibility to:', neighborhoodVisibility);
      }
      if (this.map.getLayer('neighborhoods-stroke')) {
        this.map.setLayoutProperty('neighborhoods-stroke', 'visibility', neighborhoodVisibility);
        console.log('Set neighborhoods-stroke visibility to:', neighborhoodVisibility);
      }
    },
    
    /**
     * Set clustering mode (auto, force cluster, or force points)
     */
    setClusteringMode(mode) {
      this.forceClusteringMode = mode;
      
      // Update button states
      if (this.clusterToggleControl) {
        this.clusterToggleControl.updateButtons(mode);
      }
      
      // Re-render locations with new mode
      if (this.dbLocations.length > 0 && this.showLocations) {
        this.renderLocations();
      }
      
      // Log the change
      console.log(`Clustering mode changed to: ${mode === null ? 'auto' : mode ? 'forced clustering' : 'forced points'}`);
    },
    
    /**
     * Clear current selection and drawings
     */
    clearSelection() {
      if (this.draw) {
        this.draw.deleteAll();
      }
      
      // Remove selection animations
      this.clearSelectionAnimations();
      
      this.selectedLocations = [];
      this.$emit('locations-selected', []);
    },
    
    /**
     * Animate selected location markers
     */
    animateSelectedMarkers(selectedLocations) {
      if (!this.useClusteredRendering) {
        // Only animate individual markers, not clustered points
        selectedLocations.forEach(location => {
          const locationId = location.id || location._id;
          const markerElement = document.querySelector(`[data-location-id="${locationId}"]`);
          
          if (markerElement) {
            // Add selected animation class
            markerElement.classList.add('selected-location-marker');
            
            // Add a temporary highlight effect with pulsing animation
            markerElement.classList.add('selection-highlight', 'pulse-animation');
            
            // Remove the highlight after animation but keep selected state
            setTimeout(() => {
              markerElement.classList.remove('selection-highlight', 'pulse-animation');
            }, 3000);
          }
        });
      }
      
      // Emit the selected locations to parent for left panel updates
      this.$emit('locations-selected', this.selectedLocations);
    },
    
    /**
     * Clear selection animations from all markers
     */
    clearSelectionAnimations() {
      this.selectedLocations = [];
      this.refreshSelectedMarkers();
      this.$emit('locations-selected', []);
    },
    
    /**
     * Toggle selection state of a single location
     */
    toggleLocationSelection(location) {
      const locationId = location.id || location._id;
      const existingIndex = this.selectedLocations.findIndex(
        selected => (selected.id || selected._id) === locationId
      );
      
      if (existingIndex !== -1) {
        // Location is already selected, remove it
        this.selectedLocations.splice(existingIndex, 1);
      } else {
        // Location is not selected, add it
        this.selectedLocations.push(location);
      }
      
      // Update visual state of markers
      this.refreshSelectedMarkers();
      
      // Animate the location that was just toggled
      this.animateSelectedMarkers([location]);
      
      // Emit the updated selection to parent components
      this.$emit('locations-selected', this.selectedLocations);
    },
    
    /**
     * Format time window for display
     */
    formatTimeWindow(startTime, endTime) {
      const start = startTime || '00:00';
      const end = endTime || '23:59';
      return `${start} - ${end}`;
    },

    /**
     * Show popup with auto-close functionality
     */
    showPopup(lngLat, content, options = {}) {
      // Close any existing popup
      this.closeCurrentPopup();

      // Create new popup with default options
      const popupOptions = {
        offset: 25,
        closeButton: true,
        closeOnClick: false,
        ...options
      };

      this.currentPopup = new mapboxgl.Popup(popupOptions)
        .setLngLat(lngLat)
        .setHTML(content)
        .addTo(this.map);

      // Set up auto-close after 5 seconds
      this.popupAutoCloseTimeout = setTimeout(() => {
        this.closeCurrentPopup();
      }, 5000);

      // Handle manual close to clear timeout
      this.currentPopup.on('close', () => {
        this.clearPopupTimeout();
        this.currentPopup = null;
      });

      return this.currentPopup;
    },

    /**
     * Close current popup if it exists
     */
    closeCurrentPopup() {
      if (this.currentPopup) {
        this.currentPopup.remove();
        this.currentPopup = null;
      }
      this.clearPopupTimeout();
    },

    /**
     * Clear popup auto-close timeout
     */
    clearPopupTimeout() {
      if (this.popupAutoCloseTimeout) {
        clearTimeout(this.popupAutoCloseTimeout);
        this.popupAutoCloseTimeout = null;
      }
    },

    /**
     * Recreate route visualization for PDF capture
     */
    async recreateRouteForPDF(routeData, solverType) {
      if (!this.map) return null;

      try {
        // Clear existing routes first
        this.clearRoutes();

        // Fit the map to show all locations
        if (routeData.allLocations && routeData.allLocations.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          routeData.allLocations.forEach(location => {
            bounds.extend(location);
          });
          
          // Add some padding around the bounds
          this.map.fitBounds(bounds, {
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            maxZoom: 15
          });
        }

        // Wait for map to settle
        await new Promise(resolve => setTimeout(resolve, 500));

        // Recreate the visualization based on solver type
        if (solverType === 'LAP') {
          if (routeData.facilities && routeData.demands && routeData.assignments) {
            await this.displayLAPAllocation(routeData);
          }
        } else if (solverType === 'VRP' || solverType === 'TSP') {
          if (routeData.routes && routeData.allLocations) {
            // Generate route colors
            const routeColors = this.generateRouteColors(routeData.routes.length);
            await this.displayRoutes(routeData, routeData.allLocations, routeColors);
          }
        }

        // Wait for rendering to complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        return this.$refs.mapContainer;
      } catch (error) {
        console.error('Error recreating route for PDF:', error);
        return null;
      }
    },

    /**
     * Generate colors for multiple routes
     */
    generateRouteColors(numRoutes) {
      const colors = [
        '#4285F4', // Google Blue
        '#EA4335', // Google Red
        '#FBBC05', // Google Yellow
        '#34A853', // Google Green
        '#FF6D01', // Orange
        '#46BDC6', // Teal
        '#7C4DFF', // Purple
        '#1B9E77', // Green-blue
        '#D95F02', // Orange-brown
        '#7570B3', // Purple-blue
        '#E7298A', // Pink
        '#66A61E', // Green
        '#E6AB02', // Yellow-orange
        '#A6761D', // Brown
        '#666666', // Gray
      ];
      
      return Array(numRoutes).fill().map((_, i) => colors[i % colors.length]);
    },

    /**
     * Get the map container element for PDF capture
     */
    getMapContainer() {
      return this.$refs.mapContainer;
    },
    
    /**
     * Get performance metrics for debugging and optimization
     */
    getPerformanceMetrics() {
      return PerformanceMonitor.getAllMetrics();
    },
    
    /**
     * Get performance summary for specific operations
     */
    getPerformanceSummary() {
      return {
        locationRendering: PerformanceMonitor.getPerformanceSummary('locationRendering'),
        clusteringPerformance: PerformanceMonitor.getPerformanceSummary('clusteringPerformance'),
        viewportLoading: PerformanceMonitor.getPerformanceSummary('viewportLoading'),
        cacheHitRatio: PerformanceMonitor.getCacheHitRatio(),
        memoryUsage: PerformanceMonitor.getMemoryUsage(),
        configuredThresholds: {
          locationThreshold: this.locationThreshold,
          viewportLoadingThreshold: this.viewportLoadingThreshold,
          useClusteredRendering: this.useClusteredRendering,
          enableViewportLoading: this.enableViewportLoading
        }
      };
    },
  },
  watch: {
    showLocations(newVal) {
      if (newVal) {
        this.renderLocations();
      } else {
        // Clear all location rendering when showLocations becomes false
        this.clearLocationRendering();
      }
    },
    
    locationSource() {
      // Reload locations when source changes
      this.loadDbLocations();
    },
    
    selectedCollectionId() {
      // Reload locations when collection changes
      if (this.locationSource === 'collections') {
        this.loadDbLocations();
      }
    }
  },
  beforeUnmount() {
    // Clean up timeouts
    if (this.viewportLoadTimeout) {
      clearTimeout(this.viewportLoadTimeout);
    }
    
    // Clean up popup timeout
    this.clearPopupTimeout();
    
    // Close any open popup
    this.closeCurrentPopup();
    
    // Clean up the map when component is destroyed
    if (this.map) {
      this.map.remove();
    }
    
    // Clean up global navigation function
    if (window.navigateToLocation) {
      delete window.navigateToLocation;
    }
  }
}
</script>

<style scoped>
.map-container {
  height: 100%;
  width: 100%;
}

/* Custom marker styles */
:global(.mapboxgl-marker) {
  cursor: pointer;
}

:global(.depot-marker) {
  background-color: #e74c3c;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}

:global(.location-marker) {
  background-color: #3498db;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

:global(.depot-db-marker) {
  background-color: #e74c3c;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

:global(.demand-db-marker) {
  background-color: #2ecc71;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

/* Hover effects for database markers */
:global(.depot-db-marker:hover) {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.6);
}

:global(.demand-db-marker:hover) {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(46, 204, 113, 0.6);
}

:global(.loading-indicator) {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px 15px;
  border-radius: 4px;
  z-index: 10;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s;
}

:global(.loading-indicator.visible) {
  opacity: 1;
}

/* Enhanced popup styles */
:global(.location-popup) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  max-width: 300px;
}

:global(.popup-title) {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #ecf0f1;
  padding-bottom: 8px;
}

:global(.popup-details) {
  margin-bottom: 15px;
}

:global(.popup-details p) {
  margin: 5px 0;
  font-size: 14px;
  line-height: 1.4;
}

:global(.popup-details strong) {
  color: #34495e;
}

:global(.popup-actions) {
  text-align: center;
}

:global(.popup-btn) {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

:global(.popup-btn:hover) {
  background-color: #2980b9;
}

/* Mapbox Draw custom styles */
:global(.mapbox-gl-draw_ctrl-draw-btn) {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

:global(.mapbox-gl-draw_ctrl-draw-btn:hover) {
  background-color: rgba(0, 0, 0, 0.2);
}

/* Selected location marker animations */
:global(.selected-location-marker) {
  border: 3px solid #3498db !important;
  transform-origin: center;
  z-index: 1000;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.3) !important;
}

:global(.selection-highlight) {
  animation: selectionHighlight 3s ease-out;
}

:global(.pulse-animation) {
  animation: selectedPulse 2s infinite;
}

@keyframes selectedPulse {
  0% { 
    transform: scale(1); 
    box-shadow: 0 0 0 0 rgba(52, 152, 219, 0.7);
  }
  50% { 
    transform: scale(1.15); 
    box-shadow: 0 0 0 10px rgba(52, 152, 219, 0.3);
  }
  100% { 
    transform: scale(1); 
    box-shadow: 0 0 0 0 rgba(52, 152, 219, 0);
  }
}

@keyframes selectionHighlight {
  0% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(46, 204, 113, 1);
  }
  25% { 
    transform: scale(1.3);
    box-shadow: 0 0 0 15px rgba(46, 204, 113, 0.7);
  }
  50% { 
    transform: scale(1.1);
    box-shadow: 0 0 0 20px rgba(46, 204, 113, 0.4);
  }
  100% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(46, 204, 113, 0);
  }
}

/* LAP Popup Styles */
:global(.lap-popup) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  max-width: 320px;
  padding: 12px;
  border-radius: 6px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

:global(.lap-popup h4) {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

:global(.lap-popup p) {
  margin: 4px 0;
  font-size: 0.9rem;
  color: #34495e;
  line-height: 1.4;
}

:global(.facility-popup) {
  border-left: 4px solid #e74c3c;
}

:global(.facility-popup h4) {
  color: #e74c3c;
}

:global(.demand-popup) {
  border-left: 4px solid #2ecc71;
}

:global(.demand-popup h4) {
  color: #2ecc71;
}

:global(.lap-popup strong) {
  font-weight: 600;
  color: #2c3e50;
}

/* Clustering toggle control styles */
:global(.clustering-toggle-control) {
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

:global(.clustering-toggle-btn) {
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 0;
}

:global(.clustering-toggle-btn:first-child) {
  border-radius: 4px 4px 0 0;
}

:global(.clustering-toggle-btn:last-child) {
  border-radius: 0 0 4px 4px;
}

:global(.clustering-toggle-btn:only-child) {
  border-radius: 4px;
}

:global(.clustering-toggle-btn:hover) {
  background-color: #f0f0f0;
}

:global(.clustering-toggle-btn.active) {
  background-color: #3498db;
  color: white;
}

:global(.clustering-toggle-btn.active:hover) {
  background-color: #2980b9;
}

/* Boundary selection control styles */
:global(.boundary-selection-control) {
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  margin-bottom: 10px;
}

:global(.boundary-selection-btn) {
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 0;
  border-bottom: 1px solid #e0e0e0;
}

:global(.boundary-selection-btn:first-child) {
  border-radius: 4px 4px 0 0;
}

:global(.boundary-selection-btn:last-child) {
  border-radius: 0 0 4px 4px;
  border-bottom: none;
}

:global(.boundary-selection-btn:only-child) {
  border-radius: 4px;
  border-bottom: none;
}

:global(.boundary-selection-btn:hover) {
  background-color: #f0f0f0;
}

:global(.boundary-selection-btn.active) {
  background-color: #e74c3c;
  color: white;
}

:global(.boundary-selection-btn.active:hover) {
  background-color: #c0392b;
}
</style>
